import { useState, useEffect, useRef } from 'react';
import storage from '@/utils/storage';
import { API_URL } from '@/config';
import { getClusterLogs } from '@/features/clusters';
import { EventSourcePolyfill } from 'event-source-polyfill';

interface SystemLogsOptions {
  clusterId: string;
  enabled: boolean;
}

interface LogEntry {
  message: string;
  timestamp: string;
  level: string;
  source: string;
  metadata: {
    rawLog: string;
    category: string | null;
  };
}

interface LogSummary {
  hasErrors: boolean;
  hasWarnings: boolean;
  sources: string[];
}

interface LogResponse {
  log_counter: number;
  cluster_id: string;
  timestamp: string;
  summary: LogSummary;
  logs: LogEntry[];
  __v: number;
}

export const useSystemLogs = ({ clusterId, enabled }: SystemLogsOptions) => {
  const [currentLogCounter, setCurrentLogCounter] = useState<number | null>(null);
  const [isFetchingOlderLogs, setIsFetchingOlderLogs] = useState(false);
  const [allLogs, setAllLogs] = useState<LogEntry[]>([]);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isFetchingFromSSE, setIsFetchingFromSSE] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isSystemLogsError, setIsSystemLogsError] = useState(false);
  const [logSummary, setLogSummary] = useState<LogSummary | null>(null);

  const access_token = storage.getToken();
  const teamId = storage.getTeam()?._id;

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Function to establish SSE connection with reconnect logic
  const fetchSSEData = (isAppending = true) => {
    if (!enabled || !clusterId) return;
    if (!access_token || !teamId) {
      if (!access_token) console.error('[SSE Connection] No access token found');
      if (!teamId) console.error('[SSE Connection] No team ID found');
      return;
    }

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${API_URL}/v2/cluster/${clusterId}/logs/stream`;
    const newEventSource = new EventSourcePolyfill(url, {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'X-Team': teamId,
      },
      withCredentials: false,
    });
    eventSourceRef.current = newEventSource;
    setIsFetchingFromSSE(true);

    newEventSource.onopen = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    newEventSource.onmessage = (event) => {
      const data = JSON.parse(event.data) as LogResponse;

      if (isAppending) {
        setAllLogs((prevLogs) => [...prevLogs, ...data.logs]);
      } else {
        setAllLogs([...data.logs]);
      }
      setIsFetchingFromSSE(true);
      if (data.summary) {
        setLogSummary(data.summary);
      }
      setCurrentLogCounter(data.log_counter);
      setIsFetchingOlderLogs(false);
    };

    newEventSource.onerror = (error) => {
      console.error('SSE error:', error);
      setIsSystemLogsError(true);
      newEventSource.close();
      reconnectTimerRef.current = setTimeout(() => fetchSSEData(isAppending), 5000);
    };

    return () => {
      newEventSource.close();
    };
  };

  useEffect(() => {
    fetchSSEData();
  }, [enabled, clusterId]);

  useEffect(() => {
    if (!enabled && eventSourceRef.current) {
      eventSourceRef.current.close();
    }
  }, [enabled]);

  const fetchOlderLogs = async () => {
    if (!enabled || currentLogCounter === null || currentLogCounter <= 1) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsFetchingOlderLogs(true);
    setIsLoadingLogs(true);
    let previousLogCounter = currentLogCounter - 1;
    if (previousLogCounter < 1) previousLogCounter = 1;

    try {
      const olderLogsData = await getClusterLogs(clusterId, previousLogCounter);
      if (!olderLogsData) return;
      setAllLogs((prevLogs) => [...olderLogsData.logs, ...prevLogs]);
      if (olderLogsData.summary) {
        setLogSummary(olderLogsData.summary);
      }
      setIsFetchingFromSSE(false);
      setCurrentLogCounter(previousLogCounter);
    } catch (error) {
      console.error('Error fetching older logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const closeSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  };

  return {
    allLogs,
    logSummary,
    isSystemLogsError,
    fetchSSEData,
    isFetchingOlderLogs,
    setIsFetchingOlderLogs,
    isUserScrolling,
    setIsUserScrolling,
    fetchOlderLogs,
    isFetchingFromSSE,
    isLoadingLogs,
    closeSSEConnection,
  };
};
